import { SUPPORT_EMAIL } from "./contact";

/**
 * The twelve fixed links in our DLT SMS templates (dlt_templates.csv). STPL
 * requires a link in a template to be fully static text, so each address here
 * is part of a registered template and must not be renamed: renaming one means
 * re-registering every template that carries it.
 */
export const SMS_LINK_SLUGS = [
  "signin", "renew", "pay", "invoices", "wallet", "offers",
  "optout", "security", "devices", "operators", "sites", "payments",
  // Owner 2026-10-21: DLT refuses the word "wallet"; /funds is the new name,
  // /wallet stays for SMS already sent.
  "funds",
  // Owner 2026-10-24: the operator invitation SMS (operator_join_invite) sends
  // the invited business here; it signs up with the mobile the SMS went to.
  "join",
] as const;

export type SmsLinkSlug = (typeof SMS_LINK_SLUGS)[number];

export interface SmsLinkPage {
  title: string;
  intro: string;
  steps: string[];
  /** Console screen behind "Sign in to AnkhOS"; absent for subscriber pages. */
  consoleTo?: string;
  /** Label of the consoleTo button; "Sign in to AnkhOS" when absent. */
  consoleLabel?: string;
  note?: string;
  /**
   * The REAL console screen this link stands for. When set (and its flag is on
   * where one is named), the link opens that screen -- signing in first and
   * returning there -- instead of this explanation page. Absent where no such
   * screen exists yet; the page then says what to do.
   */
  opens?: { to: string; flag?: string };
}

// 🔑 Subscriber pages never say "Wi-Fi": the same SMS goes to sponsored members.
const HOTSPOT_STEPS = [
  "Connect to your internet hotspot.",
  "Open any website. The hotspot login page appears.",
];

/** The funds an operator holds with its upstream ISP (owner 2026-10-21: "Funds", never "wallet"). */
const FUNDS_PAGE: SmsLinkPage = {
  title: "Your AnkhOS funds",
  intro: "Your available funds are low.",
  steps: ["Press Sign in to AnkhOS below.", "Open Funds and choose Add funds."],
  consoleTo: "/providers/pinorders",
  opens: { to: "/providers/pinorders", flag: "providers.pinOrders" },
};

export const SMS_LINKS: Record<SmsLinkSlug, SmsLinkPage> = {
  signin: {
    title: "Sign in to AnkhOS",
    intro: "You have been added as an operator on AnkhOS.",
    steps: [
      "Press Sign in to AnkhOS below.",
      "Sign in with the mobile number this SMS was sent to.",
      "Your sites appear on the home page.",
    ],
    consoleTo: "/",
    opens: { to: "/" },
  },
  renew: {
    title: "Renew your internet plan",
    intro: "Your plan has expired, is about to expire, or its data is used up.",
    steps: [...HOTSPOT_STEPS, "Sign in and choose Renew or Recharge.", "Pick a plan and pay online."],
    note: "You can also renew through your internet operator.",
  },
  pay: {
    title: "Retry your payment",
    intro: "A payment for your internet plan did not go through.",
    steps: [...HOTSPOT_STEPS, "Sign in and choose Recharge.", "Pay again for the same plan."],
    note: "If money was debited for the failed payment, it is refunded to the same account. "
      + "Keep the reference number from the SMS and give it to your internet operator if you need help.",
  },
  invoices: {
    title: "Your invoices",
    intro: "An invoice from Ankh Cloud Tech has been raised, or is due.",
    steps: ["Press Sign in to AnkhOS below.", "Open Billing, then Invoices from Ankh.", "View, download or pay the invoice."],
    consoleTo: "/billing/invoices",
    opens: { to: "/billing/invoices", flag: "billing.ankhInvoices" },
  },
  wallet: FUNDS_PAGE,
  funds: FUNDS_PAGE,
  offers: {
    title: "Internet plan offers",
    intro: "Use the promo code from your SMS when you recharge.",
    steps: [...HOTSPOT_STEPS, "Sign in and choose Recharge.", "Enter the promo code before you pay."],
    note: "T&C: the code is valid till the date in the SMS, once per recharge, on eligible plans only, "
      + "and cannot be exchanged for cash. To stop offer SMS, open ankhos.io/optout.",
  },
  optout: {
    title: "Stop offer SMS",
    intro: "You can stop promotional SMS from Ankh Cloud Tech (AnkhOS) in either of these ways.",
    steps: [
      "Enter your mobile number below and email us the request.",
      "Or block offers in the Communication / IT category from every sender: SMS BLOCK 6 to 1909, or dial *1909*6#.",
      "Or block all promotional SMS: SMS BLOCK PROMO to 1909, or dial *1909*50#.",
    ],
    note: "Account, login code (OTP), recharge and bill SMS are service messages. "
      + "They continue after you opt out, because they are about your own account.",
  },
  security: {
    title: "Secure your AnkhOS account",
    intro: "There was a new sign-in to your AnkhOS account.",
    steps: [
      "If it was you, nothing more is needed.",
      "If it was not, press Sign in to AnkhOS below and open Security.",
      "Change your password and check your authenticator app.",
    ],
    consoleTo: "/settings/security",
    opens: { to: "/settings/security" },
  },
  devices: {
    title: "Manage your devices",
    intro: "Your account has reached its device limit.",
    steps: [...HOTSPOT_STEPS, "Sign in and remove a device you no longer use.", "Connect the new device."],
    note: "If the login page does not offer this, ask your internet operator to remove the old device.",
  },
  operators: {
    title: "Review your operators",
    intro: "An operator was added to one of your locations.",
    steps: ["Press Sign in to AnkhOS below.", "Open Providers and check who has access.", "Disable anyone you did not add."],
    consoleTo: "/providers",
    opens: { to: "/providers" },
  },
  sites: {
    title: "Check your site",
    intro: "One of your sites stopped responding.",
    steps: [
      "Check power and the uplink cable at the site's router.",
      "Press Sign in to AnkhOS below to see the site's status.",
    ],
    consoleTo: "/",
  },
  join: {
    title: "Sign up on AnkhOS",
    intro: "Ankh Cloud Tech has invited your business to AnkhOS.",
    steps: [
      "Press Sign up on AnkhOS below.",
      "Enter the mobile number this SMS was sent to.",
      "Enter the OTP sent to that mobile, then add your business details.",
    ],
    consoleTo: "/register",
    consoleLabel: "Sign up on AnkhOS",
    note: "The invitation works once, from the mobile number it was sent to, until the date in the SMS.",
  },
  payments: {
    title: "Online payments are failing",
    intro: "Subscribers at one of your locations could not complete online payments.",
    steps: ["Press Sign in to AnkhOS below.", "Check recent payments and your payment gateway settings."],
    consoleTo: "/",
  },
};

export function validIndianMobile(s: string): boolean {
  return /^[6-9]\d{9}$/.test(s);
}

export function optOutMailto(mobile: string): string {
  const subject = "Stop offer SMS";
  const body = `Please stop promotional SMS from Ankh Cloud Tech to my mobile number ${mobile}.`;
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
